import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { overlayLayers } from '../../ui/overlayLayers';
import { buildUnsavedChangesConfirmArgs } from '../../utils/ui/unsavedChangesConfirmArgs';
import { registerUnsavedWork } from '../../utils/app/unsavedWorkRegistry';
import { noteUnsavedConfirm, noteUnsavedStay, registerDismissibleOverlay } from '../../utils/ui/overlayDismissBus';

const read = (v) => (typeof v === 'function' ? v() : Boolean(v));

/** The confirm's own Escape / scrim can reach the HOST's listener in the same
 * gesture (listener order is not ours to pick) — ignore close requests this
 * long after Stay/Leave so one Escape never re-asks. */
const RESOLVE_LATCH_MS = 350;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * useUnsavedChangesGuard — the ONE "Unsaved changes — Stay / Leave" gate for
 * create/edit drawers and typing pop-ups (RD-UI-009, KAN-580).
 *
 * Route EVERY way out of the overlay through `guardedClose` — scrim tap,
 * header ✕, footer Cancel, Escape, and any in-body "leave" link:
 *
 *   const { guardedClose, confirmNode } = useUnsavedChangesGuard({
 *     isDirty: hasChanges, onClose: requestClose, isBusy: saving,
 *   });
 *   <DrawerShell onClose={guardedClose} …/>   …   {confirmNode}
 *
 *   - busy (a save in flight) → no-op, exactly like the busy-only guards it
 *     replaces. Busy must be a flag that ALWAYS clears (a beat's own busy, a
 *     `finally`) — never one that a hung write can latch forever.
 *   - not dirty → `onClose(...args)` at once (opening a blank form and
 *     closing it never asks).
 *   - dirty → the shared confirm. Stay = nothing changes. Leave = `onClose`
 *     after the confirm's own exit, so the host still plays its exit beat
 *     (pass the modal's `requestClose`, never a parent unmount).
 *   - while the confirm is up, further `guardedClose` calls (a second scrim
 *     tap, the host's own Escape listener) are ignored.
 *
 * `isDirty` / `isBusy` take a boolean or a `() => boolean` (read at call
 * time — use the function form when dirtiness lives in a ref). `isOpen`
 * (default true): when the host closes by itself, a pending confirm is
 * dropped. `zIndex` defaults to `overlayLayers.confirmDialog`; a host that
 * can sit ABOVE that tier (e.g. a drawer over the manual-invoice builder)
 * passes `overlayLayers.confirmDialogAboveManualInvoice`.
 *
 * `runGuarded(action)` gates any other "leave" (e.g. a link that navigates
 * away from the form) with the same question.
 *
 * KAN-204: a host that passes an `isOpen` flag EXPLICITLY is registered with
 * `overlayDismissBus` while open, so a navigation that must leave it ("Open
 * Plan & billing" from anywhere) closes it through `guardedClose` — the same
 * Stay / Leave question, never a silent unmount. Stay is reported to the bus.
 */
export function useUnsavedChangesGuard({
  isDirty, onClose, isBusy = false, isOpen, zIndex = overlayLayers.confirmDialog,
}) {
  const intl = useIntl();
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(null);
  const resolvedAtRef = useRef(-Infinity);
  const latest = useRef({ isDirty, isBusy, onClose });
  latest.current = { isDirty, isBusy, onClose };
  const openRef = useRef(isOpen);
  openRef.current = isOpen;

  // The silent app-update gate must see this form's unsaved work (KAN-886).
  useEffect(() => registerUnsavedWork(
    () => (openRef.current !== undefined && !openRef.current ? false : read(latest.current.isDirty)),
  ), []);

  const runGuarded = useCallback((action) => {
    if (pendingRef.current) return;
    if (now() - resolvedAtRef.current < RESOLVE_LATCH_MS) return;
    if (read(latest.current.isBusy)) return;
    if (!read(latest.current.isDirty)) { action(); return; }
    const entry = { action };
    pendingRef.current = entry;
    setPending(entry);
  }, []);

  const guardedClose = useCallback((...args) => {
    runGuarded(() => latest.current.onClose?.(...args));
  }, [runGuarded]);

  useEffect(() => {
    if (isOpen !== undefined && !isOpen && pendingRef.current) {
      pendingRef.current = null;
      setPending(null);
    }
  }, [isOpen]);

  const registered = isOpen !== undefined && Boolean(isOpen);
  useEffect(() => {
    if (!registered) return undefined;
    return registerDismissibleOverlay(() => guardedClose());
  }, [registered, guardedClose]);

  const hasPending = Boolean(pending);
  useEffect(() => {
    if (!hasPending) return undefined;
    noteUnsavedConfirm(true);
    return () => noteUnsavedConfirm(false);
  }, [hasPending]);

  const stay = useCallback(() => {
    noteUnsavedStay();
    resolvedAtRef.current = now();
    pendingRef.current = null;
    setPending(null);
  }, []);

  const leave = useCallback(() => {
    const entry = pendingRef.current;
    resolvedAtRef.current = now();
    pendingRef.current = null;
    setPending(null);
    // A save that started under the confirm (a host key binding) wins — never
    // close mid-write.
    if (read(latest.current.isBusy)) return;
    entry?.action();
  }, []);

  const confirmNode = pending ? (
    <ConfirmDialog
      {...buildUnsavedChangesConfirmArgs(intl)}
      zIndex={zIndex}
      onConfirm={leave}
      onCancel={stay}
    />
  ) : null;

  return { guardedClose, runGuarded, confirmNode, confirmOpen: Boolean(pending) };
}

export default useUnsavedChangesGuard;
